import { ActionModal, ActionModalProps, FormError } from '@kcftech/design-system';
import { Checkbox } from 'components/forms/Checkbox';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { WarningText } from 'components/text/WarningText';
import { useToggle } from 'custom_hooks/useToggle';

export type ConfirmModalProps = Omit<
    ActionModalProps,
    | 'actionButtonText'
    | 'onAction'
    | 'showActionButton'
    | 'actionable'
    | 'showButtons'
    | 'children'
> &
    Readonly<{
        /**
         * The text for the action button (defaults to `'Confirm'`).
         */
        actionButtonText?: string;
        /**
         * Text for close button (defaults to `'Cancel'`).
         */
        closeButtonText?: string;
        /**
         * Callback for when action button is clicked.
         */
        onAction?: () => void;
        /**
         * Sets action button visibility.
         */
        showActionButton?: boolean;
        /**
         * If action button is enabled (defaults to `true`).
         */
        actionable?: boolean;
        /**
         * The text for the warning (defaults to 'This action cannot be undone.').
         * The warning text will be hidden if `false` is supplied.
         */
        warningText?: string | false;
        /**
         * The text for the action being done
         *
         * `Are you sure you want to [actionText]`
         */
        actionText: string;
        /**
         * The text by the checkbox:
         *
         * `Yes, [checkText]`
         *
         * If `false`, the checkbox will not be displayed.
         */
        checkText: string | false;
        /**
         * Error to be displayed.
         */
        error?: string;
        /**
         * If an action is being process (defaults to `false`).
         * When enabled, displays spinner and disables action and close buttons.
         */
        isLoading?: boolean;
        /**
         * Type of action button (defaults to `'danger'`).
         */
        actionType?: 'default' | 'danger';
    }>;

export function ConfirmModal({
    closeButtonText = 'Cancel',
    actionButtonText = 'Confirm',
    actionText,
    onAction,
    showActionButton: showDeleteButton = true,
    actionable = true,
    closable = true,
    checkText,
    warningText,
    width = 700,
    height = 350,
    error,
    isLoading = false,
    actionType = 'danger',
    ...rest
}: ConfirmModalProps) {
    const [checked, toggle] = useToggle();
    const isChecked = checked || checkText === false;
    const checkboxClass = actionType === 'danger' ? 'delete-check' : '';

    const onConfirm = () => {
        if (isChecked && onAction) {
            onAction();
        }
    };

    return (
        <ActionModal
            data-testid="confirm-modal"
            {...rest}
            width={width}
            height={height}
            closeButtonText={closeButtonText}
            actionButtonText={actionButtonText}
            onAction={onConfirm}
            showActionButton={showDeleteButton}
            actionable={actionable && isChecked && !isLoading}
            closable={closable && !isLoading}
            actionType={actionType}
        >
            <SpinnerArea isLoading={isLoading}>
                {warningText !== false && <WarningText text={warningText} />}
                <div data-testid="delete-text">Are you sure you want to {actionText}</div>
                {checkText !== false && (
                    <div className={`${checkboxClass} py-10`}>
                        <Checkbox
                            checked={checked}
                            onChange={toggle}
                            label={`Yes, ${checkText}`}
                        />
                    </div>
                )}
                <div className="py-10">
                    <FormError message={error} />
                </div>
            </SpinnerArea>
        </ActionModal>
    );
}
